import { useSearchParams } from "react-router-dom";
import { CTASection, SectionHeading } from "@/components/sections";
import { Stagger, StaggerItem } from "@/components/primitives";
import { ProductHero, RequestInfo } from "@/components/product-parts";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/LangLink";
import { ArrowUpRight } from "lucide-react";
import { LUMAVEN_PREVENTIVE_CARDS, LUMAVEN_ANTITROMBO } from "@/lib/lumaven";

const LumCardImage = ({ cover, name, testid }) => (
  <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-cloud border border-[#E7EDF2]" data-testid={testid}>
    <img src={cover} alt={name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
  </div>
);


const LUMAVEN_FAQ = [
  ["Che differenza c'è tra calze preventive e terapeutiche?", "Le calze preventive offrono una compressione leggera per gambe affaticate e per chi resta a lungo in piedi o seduto; le terapeutiche hanno una compressione più elevata, misurata in mmHg e indicata dal medico. Le preventive non sostituiscono le terapeutiche quando è necessaria una terapia."],
  ["A cosa servono le calze antitrombo?", "Sono calze a compressione graduata pensate per favorire il ritorno venoso in situazioni di ridotta mobilità (ad esempio degenza o post-operatorio), nella profilassi della trombosi venosa profonda. L'indicazione e la durata d'uso spettano al personale sanitario."],
  ["Quando indossare le calze preventive?", "In genere durante la giornata, soprattutto in caso di lunghe permanenze in piedi o seduti, viaggi prolungati o gambe pesanti. Si indossano al mattino e si rimuovono la sera."],
  ["Come si mantengono?", "Lavaggio delicato in acqua tiepida con sapone neutro, senza strizzare, con asciugatura in orizzontale lontano da fonti di calore. Vanno sostituite quando perdono elasticità."],
  ["Come scelgo la misura giusta?", "La taglia dipende dalle misure della gamba, non dal numero di scarpa. Per un risultato corretto è consigliata una misurazione professionale presso MEDISISTEM."],
];

const PREVENTIVE_INTRO =
  "La gamma LUMAVEN nasce per accompagnare il benessere quotidiano delle gambe attraverso soluzioni confortevoli, curate nell'estetica e adatte a differenti esigenze di vestibilità. Le linee 70 e 140 comprendono diverse esecuzioni, dai gambaletti ai collant, mentre la famiglia Comfort propone calzini dall'aspetto discreto, pensati soprattutto per il pubblico maschile ma utilizzabili anche dalla donna.";

const LUMAVEN_FAMILIES = [
  { id: "preventive", label: "Calze preventive" },
  { id: "antitrombo", label: "Antitrombo" },
];

const Lumaven = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("fam") || "tutti";
  const active = LUMAVEN_FAMILIES.some((f) => f.id === raw) ? raw : "tutti";
  const showPrev = active === "tutti" || active === "preventive";
  const showAnti = active === "tutti" || active === "antitrombo";

  const chips = [{ id: "tutti", label: "Tutti i prodotti" }, ...LUMAVEN_FAMILIES];

  const selectFam = (id) => {
    if (id === "tutti") setSearchParams({});
    else setSearchParams({ fam: id });
    requestAnimationFrame(() => {
      const el = document.getElementById("lumaven-gamma");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <>
      <ProductHero
        theme="green"
        badge="Marchio di Proprietà Medisistem"
        name="LUMAVEN"
        logo="/brands/lumaven.png"
        layout="split"
        tagline="La linea di proprietà Medisistem dedicata alla prevenzione e alla profilassi antitrombo. Qualità controllata in ogni fase, dalla progettazione all'assistenza."
        testid="lumaven-hero"
      />

      {/* Filtri per famiglia */}
      <section className="pt-14 lg:pt-20 bg-white" data-testid="lumaven-filters-section">
        <div className="container-x">
          <SectionHeading
            size="cat"
            eyebrow="Gamma completa"
            title="La gamma LUMAVEN"
            intro="Filtra per famiglia per trovare più rapidamente il prodotto giusto, oppure sfoglia l'intera gamma. Clicca su un prodotto per aprire la scheda completa."
          />
          <div className="mt-8 flex flex-wrap gap-2.5" role="group" aria-label="Filtra per famiglia" data-testid="lumaven-filters">
            {chips.map((c) => {
              const on = c.id === active;
              return (
                <a
                  key={c.id}
                  href={c.id === "tutti" ? "?" : `?fam=${c.id}`}
                  onClick={(e) => { e.preventDefault(); selectFam(c.id); }}
                  role="button"
                  aria-pressed={on}
                  data-testid={`lumaven-filter-${c.id}`}
                  className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2 ${
                    on ? "border-green bg-green text-white" : "border-[#E7EDF2] text-navy/70 hover:border-green hover:text-green"
                  }`}
                >
                  {c.label}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <div id="lumaven-gamma" className="scroll-mt-28">
        {showPrev && (
          <section className="py-16 lg:py-24 bg-white" data-testid="lumaven-preventive">
            <div className="container-x">
              <SectionHeading
                size="cat"
                maxW="max-w-3xl"
                eyebrow="Calze preventive"
                title="Le linee preventive LUMAVEN"
                intro={PREVENTIVE_INTRO}
              />
              <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {LUMAVEN_PREVENTIVE_CARDS.map((c) => (
                  <StaggerItem key={c.id} className="h-full">
                    <Link
                      to={c.to}
                      data-testid={`lumaven-card-${c.id}`}
                      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E7EDF2] bg-white p-5 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-green hover:shadow-[0_24px_50px_-18px_rgba(15,25,35,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2"
                    >
                      <LumCardImage cover={c.cover} name={c.name} testid={`lumaven-cover-${c.id}`} />
                      <div className="mt-5 flex flex-1 flex-col">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-green-deep">Linea preventiva</span>
                        <h3 className="mt-1.5 font-serif text-2xl font-bold text-navy">{c.name}</h3>
                        <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-green transition-all group-hover:gap-3">
                          Scopri la linea <ArrowUpRight size={15} />
                        </span>
                      </div>
                    </Link>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </section>
        )}

        {showAnti && (
          <section className="py-16 lg:py-24 bg-cloud" data-testid="lumaven-antitrombo">
            <div className="container-x">
              <SectionHeading
                size="cat"
                eyebrow="Linea antitrombo"
                title="Sicurezza nella profilassi"
                intro="Dispositivi a compressione graduata per la profilassi antitromboembolica, in ambito ospedaliero e domiciliare."
              />
              <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <StaggerItem className="h-full">
                  <Link
                    to={`/prodotti/lumaven/${LUMAVEN_ANTITROMBO.slug}`}
                    data-testid="lumaven-card-lum-antitrombo"
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E7EDF2] bg-white p-5 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-green hover:shadow-[0_24px_50px_-18px_rgba(15,25,35,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2"
                  >
                    <LumCardImage cover="/products/lumaven-antitrombo/coscia.jpg" name={LUMAVEN_ANTITROMBO.name} testid="lumaven-cover-lum-antitrombo" />
                    <div className="mt-5 flex flex-1 flex-col">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-green-deep">Linea antitrombo</span>
                      <h3 className="mt-1.5 font-serif text-2xl font-bold text-navy">{LUMAVEN_ANTITROMBO.name}</h3>
                      <p className="mt-1 text-sm font-medium text-navy/70">{LUMAVEN_ANTITROMBO.subtitle}</p>
                      <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed">
                        Compressione graduata 18–21 mmHg con sistema autoreggente Soft Touch. Tre esecuzioni: gambaletto, calza alla coscia e monocollant ambidestro.
                      </p>
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-green transition-all group-hover:gap-3">
                        Scopri la scheda prodotto <ArrowUpRight size={15} />
                      </span>
                    </div>
                  </Link>
                </StaggerItem>
              </Stagger>
              <RequestInfo theme="green" />
            </div>
          </section>
        )}
      </div>

      <section className="py-20 lg:py-28 bg-white" data-testid="lumaven-faq">
        <div className="container-x max-w-3xl">
          <SectionHeading size="cat" maxW="max-w-3xl" eyebrow="Domande frequenti" title="Le risposte alle domande più comuni" />
          <Accordion type="single" collapsible className="mt-10">
            {LUMAVEN_FAQ.map(([q, a], i) => (
              <AccordionItem key={q} value={`faq-${i}`}>
                <AccordionTrigger data-testid={`faq-${i}`} className="text-left text-navy">{q}</AccordionTrigger>
                <AccordionContent><p className="text-sm text-navy/80 leading-relaxed">{a}</p></AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Lumaven;
